import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import classNames from "classnames";
import { BoxIcon, CheckIcon, ChevronDownIcon, ClockIcon, MoneyIcon, RateIcon, StarIcon } from "@/assets/icons";
import cls from "./styles.module.scss";

/*
 * Статика по макету E3 «полная» — цифры прототипа, парк подключён.
 * Живой статистики в демо нет (решение 07.09), поэтому данные лежат здесь.
 */
type PeriodKey = "day" | "week" | "month";

interface Bar {
  d: string;
  base: number;
  park: number;
}

const WEEK: Bar[] = [
  { d: "07.04–13.04", base: 6120, park: 2400 },
  { d: "14.04–20.04", base: 5980, park: 2100 },
  { d: "21.04–27.04", base: 6420, park: 2550 },
  { d: "28.04–04.05", base: 6445, park: 3078 },
];
const DAY: Bar[] = [
  { d: "Пт", base: 980, park: 0 },
  { d: "Сб", base: 1240, park: 0 },
  { d: "Вс", base: 0, park: 0 },
  { d: "Пн", base: 860, park: 0 },
  { d: "Вт", base: 1120, park: 0 },
  { d: "Ср", base: 990, park: 0 },
  { d: "Сегодня", base: 1255, park: 0 },
];
const MONTH: Bar[] = [
  { d: "Янв", base: 24800, park: 6200 },
  { d: "Фев", base: 23100, park: 5400 },
  { d: "Мар", base: 26700, park: 8900 },
  { d: "Апр", base: 25300, park: 9578 },
];

const PERIODS: Record<PeriodKey, { bars: Bar[]; workSec: number; lab: string; cap: string }> = {
  day: { bars: DAY, workSec: 28800, lab: "Доход за сегодня", cap: "Твоя статистика за день" },
  week: { bars: WEEK, workSec: 138600, lab: "Доход за неделю", cap: "Твоя недельная статистика" },
  month: { bars: MONTH, workSec: 594000, lab: "Доход за месяц", cap: "Твоя статистика за месяц" },
};

const BD_PARK = [
  { l: "Цель от Парка БК", v: 3000, wide: false },
  { l: "Розыгрыш БК", v: 0, wide: false },
  { l: "Доход по реферальной программе БК", v: 78, wide: true },
  { l: "Соревнование БК", v: 0, wide: false },
  { l: "Заруба БК", v: 0, wide: false },
];
const BD_EATS = [
  { l: "Вознаграждение от Яндекс Еды", v: 6240, wide: true },
  { l: "Чаевые", v: 415, wide: false },
  { l: "Бонусы и корректировки", v: 620, wide: false },
  { l: "Корректировки на слоте", v: 0, wide: false },
  { l: "Комиссия партнёра", v: -248.03, wide: false },
  { l: "Налог", v: -581.54, wide: false },
];

const rub = (n: number) => Math.round(n).toLocaleString("ru-RU");
const rubc = (n: number) =>
  n.toLocaleString("ru-RU", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
const round2 = (n: number) => Math.round(n * 100) / 100;
const fmtHM = (sec: number) => {
  const hh = Math.floor(sec / 3600);
  const mm = Math.round((sec % 3600) / 60);
  return `${hh} ч${mm ? ` ${mm} мин` : ""}`;
};

export const StatsPage = () => {
  const { t } = useTranslation();
  const [period, setPeriod] = useState<PeriodKey>("week");
  const [sel, setSel] = useState<Partial<Record<PeriodKey, number>>>({});
  const [referral, setReferral] = useState(true);
  const [open, setOpen] = useState(true);

  const per = PERIODS[period];
  const { bars } = per;
  const selected = sel[period] ?? bars.length - 1;
  const last = bars[selected];
  const total = last.base + last.park;
  const baseRatio = last.base / 6445.43;
  const parkRatio = last.park / 3078;
  const refIncome = round2(78 * baseRatio);
  const shownIncome = last.base + (referral ? refIncome : 0);
  const perHour = per.workSec ? Math.round((shownIncome * 3600) / per.workSec) : 0;
  const hoursRatio = per.workSec / 138600;
  const doneCount = Math.round(112 * hoursRatio);
  const maxT = Math.max(...bars.map((b) => b.base + b.park), 1);

  const chips = [
    ...BD_PARK.map((x) => ({ ...x, park: true, v: round2(x.v * parkRatio) })),
    ...BD_EATS.map((x) => ({ ...x, park: false, v: round2(x.v * baseRatio) })),
  ].filter((x) => x.v !== 0);

  return (
    <section className={cls.page}>
      <div className={cls.head}>
        <div>
          <span className={cls.eyebrow}>{t("stats.eyebrow", { service: "Еда" })}</span>
          <div className={cls.ttl}>{t("stats.title")}</div>
        </div>
      </div>

      <div className={cls.seg}>
        <span
          className={cls.ind}
          style={{
            transform: `translateX(calc(${["day", "week", "month"].indexOf(period) * 100}% + ${
              ["day", "week", "month"].indexOf(period) * 2
            }px))`,
          }}
        />
        {(["day", "week", "month"] as PeriodKey[]).map((p) => (
          <button
            key={p}
            type="button"
            aria-selected={period === p}
            onClick={() => setPeriod(p)}
          >
            {{ day: "День", week: "Неделя", month: "Месяц" }[p]}
          </button>
        ))}
      </div>

      <div className={classNames(cls.card, cls.incomeCard)}>
        <div className={cls.stHead}>
          <div>Доход:</div>
          <div>
            Яндекс Еда + <strong>Богатый Курьер</strong>
          </div>
        </div>
        <div className={cls.stTotal}>{rubc(total)} ₽</div>
        <div className={cls.bars}>
          {bars.map((b, i) => {
            const tot = b.base + b.park;
            const empty = tot <= 0;
            const h = empty ? 12 : Math.round(48 + (tot / maxT) * 96);
            const share = tot > 0 ? (b.park / tot) * 100 : 0;
            return (
              <button
                key={b.d}
                type="button"
                className={cls.barcol}
                aria-selected={i === selected}
                onClick={() => setSel((s) => ({ ...s, [period]: i }))}
              >
                <span className={cls.cap}>{empty ? "—" : rub(tot)}</span>
                <span className={classNames(cls.stack, empty && cls.empty)} style={{ height: h }}>
                  {b.park > 0 && (
                    <span className={cls.park} style={{ height: `${share}%` }}>
                      {rub(b.park)}
                    </span>
                  )}
                  {b.base > 0 && <span className={cls.base}>{rub(b.base)}</span>}
                </span>
                <span className={cls.dt}>{b.d}</span>
              </button>
            );
          })}
        </div>
        <div className={cls.legend}>
          <span className={cls.it}>
            <i style={{ background: "var(--accent)" }} />
            Доп. доход от Парка БК
          </span>
        </div>
      </div>

      <ul className={cls.chipgrid}>
        {chips.map((x) => (
          <li key={x.l} className={classNames(cls.chip, x.park && cls.chipPark, x.wide && cls.chipWide)}>
            <span>{x.l}</span>
            <strong>{rubc(x.v)} ₽</strong>
          </li>
        ))}
      </ul>

      <div className={cls.acc} data-open={open}>
        <button type="button" className={cls.accBtn} onClick={() => setOpen((o) => !o)}>
          <span className={cls.accTtl}>
            <RateIcon />
            Детализированная статистика
          </span>
          <span className={cls.chev}>
            <ChevronDownIcon />
          </span>
        </button>
        <div className={cls.accBody}>
          <div>
            <div className={cls.inner}>
              <div className={cls.capText}>
                {per.cap} · {last.d}
              </div>
              <dl className={cls.statlist}>
                <div className={cls.detrow}>
                  <dt>
                    <BoxIcon />
                    Завершённые заказы
                  </dt>
                  <dd>{doneCount}</dd>
                </div>
                <div className={cls.detrow}>
                  <dt>
                    <MoneyIcon />
                    {per.lab}
                  </dt>
                  <dd>{rub(shownIncome)} ₽</dd>
                </div>
                <div className={cls.detrow}>
                  <dt>
                    <ClockIcon />
                    Время на заказах
                  </dt>
                  <dd>{fmtHM(per.workSec)}</dd>
                </div>
                <div className={cls.detrow}>
                  <dt>
                    <RateIcon />
                    Доход в час
                  </dt>
                  <dd>{perHour} ₽</dd>
                </div>
              </dl>
              <div className={cls.tothl}>
                <StarIcon />
                <span>
                  Общий доход в Яндекс Еде с Парком БОГАТЫЙ КУРЬЕР Еда: <b>{rub(total)} ₽</b>
                </span>
              </div>
              <div
                className={cls.checkline}
                role="checkbox"
                tabIndex={0}
                aria-checked={referral}
                onClick={() => setReferral((r) => !r)}
                onKeyDown={(e) => {
                  if (e.key === "Enter" || e.key === " ") setReferral((r) => !r);
                }}
              >
                <span className={cls.box}>
                  <CheckIcon />
                </span>
                <span>С реферальной программой</span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};
